import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, formatCzk, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Trash2, Plus, Repeat, MapPin, FileDown, Building2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];
const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Apartments = () => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const loadAll = async () => {
    const [a, r, i] = await Promise.all([
      api.get("/addresses"),
      api.get("/recurring"),
      api.get("/invoices/me"),
    ]);
    setAddresses(a.data);
    setRecurring(r.data);
    setInvoices(i.data);
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-6 h-6 text-sky-600" />
          <div className="tiny-label">Pro Airbnb &amp; ubytování</div>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Správa apartmánů</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Spravujte více adres, plánujte pravidelné svozy a stahujte měsíční přehledy.</p>

        <Tabs defaultValue="addresses" className="mt-8">
          <TabsList>
            <TabsTrigger value="addresses" data-testid="apt-tab-addresses">Adresy ({addresses.length})</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="apt-tab-recurring">Pravidelné svozy ({recurring.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="apt-tab-invoices">Měsíční přehledy ({invoices.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="addresses" className="mt-6">
            <AddressList addresses={addresses} onReload={loadAll} />
          </TabsContent>

          <TabsContent value="recurring" className="mt-6">
            <RecurringList recurring={recurring} addresses={addresses} onReload={loadAll} />
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <InvoicesList invoices={invoices} />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

const AddressList = ({ addresses, onReload }) => {
  const [form, setForm] = useState({ label: "", address: "", city: "", zip: "", notes: "" });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePick = (pick) => {
    setForm((f) => ({
      ...f,
      address: pick.road || pick.display,
      city: pick.city || f.city,
      zip: pick.postcode || f.zip,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/addresses", form);
      toast.success("Adresa přidána");
      setForm({ label: "", address: "", city: "", zip: "", notes: "" });
      setOpen(false);
      await onReload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Smazat adresu (a všechny její pravidelné svozy)?")) return;
    await api.delete(`/addresses/${id}`);
    toast.success("Adresa smazána");
    onReload();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="apt-add-address-btn"><Plus className="w-4 h-4 mr-2" /> Přidat adresu</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nová adresa</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Název (např. Apartmán Karlův most)</Label>
                <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="apt-address-label" />
              </div>
              <div>
                <Label>Adresa</Label>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  onPick={handlePick}
                  data-testid="apt-address-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Město</Label>
                  <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="apt-address-city" />
                </div>
                <div>
                  <Label>PSČ</Label>
                  <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="apt-address-zip" />
                </div>
              </div>
              <div>
                <Label>Poznámka pro prádelnu</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="apt-address-notes" />
              </div>
              <Button type="submit" disabled={saving} className="w-full" data-testid="apt-address-save">
                {saving ? "Ukládám…" : "Uložit adresu"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {addresses.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">Zatím žádné adresy. Přidejte první apartmán.</Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <Card key={a.id} className="p-5" data-testid={`apt-address-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-700 grid place-items-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-slate-900">{a.label}</div>
                    <div className="text-sm text-slate-600">{a.address}</div>
                    <div className="text-xs text-slate-500">{a.city} {a.zip}</div>
                    {a.notes && <div className="text-xs text-slate-500 italic mt-1">"{a.notes}"</div>}
                  </div>
                </div>
                <button onClick={() => remove(a.id)} className="text-slate-400 hover:text-rose-600" data-testid={`apt-address-delete-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const RecurringList = ({ recurring, addresses, onReload }) => {
  const [form, setForm] = useState({
    address_id: "", day_of_week: "1", hour: "9", minute: "0",
    kg: "8", service_type: "premium", express_type: "none", notes: "", preferences: [],
  });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address_id) { toast.error("Vyberte adresu"); return; }
    setSaving(true);
    try {
      await api.post("/recurring", {
        ...form,
        day_of_week: parseInt(form.day_of_week),
        hour: parseInt(form.hour),
        minute: parseInt(form.minute),
        kg: parseFloat(form.kg),
      });
      toast.success("Pravidelný svoz vytvořen");
      setOpen(false);
      await onReload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    await api.put(`/recurring/${r.id}`, { active: !r.active });
    onReload();
  };

  const remove = async (id) => {
    if (!confirm("Smazat pravidelný svoz?")) return;
    await api.delete(`/recurring/${id}`);
    onReload();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="apt-add-recurring-btn"><Plus className="w-4 h-4 mr-2" /> Nový pravidelný svoz</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pravidelný svoz</DialogTitle>
            </DialogHeader>
            {addresses.length === 0 ? (
              <div className="text-sm text-slate-600 py-4">
                Nejprve přidejte alespoň jednu adresu v záložce <strong>Adresy</strong>.
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Adresa</Label>
                  <Select value={form.address_id} onValueChange={(v) => setForm({ ...form, address_id: v })}>
                    <SelectTrigger data-testid="apt-recurring-address"><SelectValue placeholder="Vyberte adresu…" /></SelectTrigger>
                    <SelectContent>
                      {addresses.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.label} · {a.city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Den</Label>
                    <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                      <SelectTrigger data-testid="apt-recurring-dow"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Hodina</Label>
                    <Input type="number" min="0" max="23" value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} data-testid="apt-recurring-hour" />
                  </div>
                  <div>
                    <Label>Minuta</Label>
                    <Input type="number" min="0" max="59" step="15" value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} data-testid="apt-recurring-min" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>kg</Label>
                    <Input type="number" min="5" step="0.5" value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })} data-testid="apt-recurring-kg" />
                  </div>
                  <div>
                    <Label>Služba</Label>
                    <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                      <SelectTrigger data-testid="apt-recurring-service"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard 60 Kč/kg</SelectItem>
                        <SelectItem value="premium">Premium 80 Kč/kg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Poznámka</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="např. povlečení a ručníky, klíče v trezoru…" data-testid="apt-recurring-notes" />
                </div>
                <Button type="submit" disabled={saving} className="w-full" data-testid="apt-recurring-save">
                  {saving ? "Ukládám…" : "Vytvořit svoz"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {recurring.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">
          Žádné pravidelné svozy. Vytvořte první a my budeme automaticky objednávat každý týden.
        </Card>
      ) : (
        <div className="grid gap-3">
          {recurring.map((r) => (
            <Card key={r.id} className="p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 grid place-items-center">
                  <Repeat className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{r.address_label}</div>
                  <div className="text-sm text-slate-500">
                    Každý {DAYS[r.day_of_week]} v {String(r.hour).padStart(2, "0")}:{String(r.minute).padStart(2, "0")} · {r.kg} kg · {r.service_type === "premium" ? "Premium" : "Standard"}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">Další: {new Date(r.next_pickup_at).toLocaleString("cs-CZ")}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleActive(r)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                  data-testid={`apt-recurring-toggle-${r.id}`}
                >
                  {r.active ? "Aktivní" : "Pozastaveno"}
                </button>
                <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-rose-600" data-testid={`apt-recurring-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const InvoicesList = ({ invoices }) => {
  const downloadPdf = async (month) => {
    const token = localStorage.getItem("pranidomu_token");
    const r = await fetch(`${REACT_APP_BACKEND_URL}/api/invoices/me/${month}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { toast.error("Nepodařilo se stáhnout PDF"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pranidomu-${month}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (invoices.length === 0) {
    return <Card className="p-10 text-center text-slate-500">Žádné dokončené objednávky pro fakturaci.</Card>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {invoices.map((i) => (
        <Card key={i.month} className="p-5 flex items-center justify-between gap-4" data-testid={`apt-invoice-${i.month}`}>
          <div>
            <div className="font-display font-bold text-slate-900">{i.month}</div>
            <div className="text-sm text-slate-500">{i.orders} objednávek · {formatCzk(i.total)}</div>
          </div>
          <Button variant="outline" onClick={() => downloadPdf(i.month)} data-testid={`apt-invoice-pdf-${i.month}`}>
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
        </Card>
      ))}
    </div>
  );
};

export default Apartments;
